import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { fromJS } from 'immutable';
import { takeRight } from 'lodash';
import { Account } from '../account-data/account.model';
import { User } from '../account-data/user.model';
import { FILTER_REGEX, FILTER_STRINGS, WindowRefService } from 'i2g-ng-utils';
import { LoggerService } from '../shared/services/logger/logger.service';
import { LoggerStore } from '../shared/services/logger/logger.store';
import { take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EnvironmentsEnum } from '../../../environments/environments.enum';

@Injectable()
export class ErrorService {
  private _window: any;
  private _userDetails: { userId: string; accountId: string; email: string };
  private _initialized: boolean;

  constructor(
    private _loggerStore: LoggerStore,
    private _loggerService: LoggerService,
    private _store: Store<any>,
    windowRefService: WindowRefService
  ) {
    this._window = windowRefService.nativeWindow;

    if (environment.raygunAPIKey) {
      this._window.rg4js('apiKey', environment.raygunAPIKey);
      this._window.rg4js('enableCrashReporting', true);
      this._window.rg4js('groupingKey', this._groupingKey.bind(this));
      this._window.rg4js('options', {
        debugMode: environment.debugMode,
        ignore3rdPartyErrors: true,
      });
      this._window.rg4js('setVersion', environment.version);

      // Filter sensitive data
      this._window.rg4js('setFilterScope', 'all');

      this._window.rg4js('filterSensitiveData', [
        ...FILTER_REGEX,
        ...FILTER_STRINGS,
      ]);
    }
  }

  init(account: Account, user: User) {
    if (environment.raygunAPIKey) {
      if (account && user) {
        this._userDetails = {
          userId: user.userId,
          accountId: account.accountId,
          email: user.email,
        };

        this._window.rg4js('setUser', {
          identifier: this._userDetails.userId,
          isAnonymous: false,
          email: user.email,
          firstName: user.name, // TODO: review this, we only get 'name' from account server
          fullName: user.name, // TODO: review this, we only get 'name' from account server
          uuid: this._userDetails.accountId,
        });
      } else if (this._window.rg4js('getRaygunInstance')) {
        // TODO: find out why `this._window.rg4js('getRaygunInstance')` would be `undefined`
        this._window.rg4js('getRaygunInstance').resetAnonymousUser();
      }

      this._initialized = true;
    } else if (environment.env === EnvironmentsEnum.localdev) {
      this._initialized = true;
    }
  }

  alertSystem(error: Error, customData?: any) {
    if (this._initialized) {
      if (environment.env !== EnvironmentsEnum.production) {
        console.error(error);
      }

      if (environment.raygunAPIKey) {
        this._loggerService.logError(error);

        const logs = takeRight(this._loggerStore.getLogs(), 20);

        this._store.pipe(take(1)).subscribe((storeSnapshot: any) => {
          this._window.rg4js('send', {
            error: error,
            customData: {
              userDetails: this._userDetails,
              ...customData,
              logs,
              storeSnapshot: fromJS(storeSnapshot).toJS(),
            },
          });
        });
      }
    }
  }

  private _groupingKey(message, exception, options) {
    const customData = options.customData;

    if (customData && customData.customGroupingKey) {
      return customData.customGroupingKey;
    }

    return null;
  }
}
